import { GraphQLError } from "graphql";
import { Text } from "north.js";
import React, { useMemo } from "react";

import { HardwareRequestStatus, HardwareSku } from "src/api/types.generated";
import { errorToast, Spinner } from "src/shared/components";
import HardwareRequestsHistoryList from "src/shared/components/Hardware/RequestsList";
import { EVENT_ID } from "src/shared/constants/event";
import { buildRequestDataById } from "src/shared/utils/hardware/buildRequestDataById";
import { filterHardwareRequestsBySearch } from "src/shared/utils/hardware/filterHardwareRequestsBySearch";
import { processRequests } from "src/shared/utils/hardware/processRequests";
import globalConstants from "src/theme/globalConstants";

import { useGetHackerHardwareRequestsQuery } from "../graphql/getHackerHardwareRequests.generated";
import { useUpdateHackerHardwareRequestByHackerMutation } from "../graphql/updateHackerHardwareRequest.generated";
import { getRandomHardwareLoadingMessage } from "../inventory/shared/loadingTextUtil";

import StatusFilterTiles from "./StatusFilterTiles";

type THackerRequestHistoryProps = {
  items: Pick<
    HardwareSku,
    | "code"
    | "image"
    | "type"
    | "name"
    | "location"
    | "description"
    | "category_id"
  >[];
  selectedStatuses: Set<HardwareRequestStatus>;
  query?: string;
  onToggleStatus: (status: HardwareRequestStatus) => void;
  onClearAllStatuses: () => void;
  onSelectAllStatuses: () => void;
};

const HackerRequestHistory = ({
  items,
  selectedStatuses,
  query,
  onToggleStatus,
  onClearAllStatuses,
  onSelectAllStatuses,
}: THackerRequestHistoryProps) => {
  const {
    data: hackerHardwareRequestsData,
    loading,
    error,
  } = useGetHackerHardwareRequestsQuery({
    variables: {
      where: {
        event_id: { equals: EVENT_ID },
        status: { not: { equals: HardwareRequestStatus.Waiting } },
      },
    },
  });

  const [updateHackerHardwareRequestByHacker] =
    useUpdateHackerHardwareRequestByHackerMutation();

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<HardwareRequestStatus, number>> = {};
    hackerHardwareRequestsData?.myHardwareRequests?.forEach((request) => {
      counts[request.status] = (counts[request.status] ?? 0) + 1;
    });
    return counts;
  }, [hackerHardwareRequestsData]);

  const hardwareRequests = useMemo(() => {
    if (!hackerHardwareRequestsData?.myHardwareRequests || !items) return [];

    const filteredByStatus = hackerHardwareRequestsData.myHardwareRequests
      .slice()
      .filter((request) =>
        selectedStatuses.size > 0 ? selectedStatuses.has(request.status) : true
      )
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    const sortedRequests = filterHardwareRequestsBySearch(
      filteredByStatus,
      query,
      items
    );

    return processRequests({
      data: sortedRequests,
      items,
      userType: "hacker",
      getButtons: () => {
        return [];
      },
    });
  }, [hackerHardwareRequestsData, items, selectedStatuses, query]);

  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  // Handler for status updates (hackers can only cancel waiting requests)
  const handleUpdateRequestStatus = async (
    requestId: number,
    status: HardwareRequestStatus
  ) => {
    try {
      await updateHackerHardwareRequestByHacker({
        variables: {
          id: requestId,
          status,
        },
      });
    } catch (e) {
      errorToast((e as GraphQLError)?.message);
    }
  };

  // Show loading state
  if (loading) {
    return <Spinner text={loadingMessage} />;
  }

  // Show error state
  if (error) {
    return <div>Sorry, something went wrong!</div>;
  }

  return (
    <>
      <StatusFilterTiles
        statusCounts={statusCounts}
        selectedStatuses={selectedStatuses}
        onToggleStatus={onToggleStatus}
        onClearAllStatuses={onClearAllStatuses}
        onSelectAllStatuses={onSelectAllStatuses}
      />
      {!hackerHardwareRequestsData?.myHardwareRequests?.length ? (
        <Text
          type="body1"
          style={{ color: globalConstants?.color?.textSecondary as string }}
        >
          No requests found.
        </Text>
      ) : hardwareRequests.length === 0 ? (
        <Text
          type="body1"
          style={{ color: globalConstants?.color?.textSecondary as string }}
        >
          No requests match the selected filters.
        </Text>
      ) : (
        <HardwareRequestsHistoryList
          hardwareRequests={hardwareRequests.map((request) => ({
            ...request,
            requestDataById: buildRequestDataById(
              request.items.map((item) => item.requestId),
              hackerHardwareRequestsData?.myHardwareRequests
            ),
          }))}
          userType="hacker"
          onUpdateStatus={handleUpdateRequestStatus}
        />
      )}
    </>
  );
};

export default HackerRequestHistory;
